/**
 * 명세서 한 장 — 표시는 `SalarySlip` 이 한다(담당자 시뮬레이터와 **같은 컴포넌트**).
 * 이 파일은 불러오기·공개 여부 판단만 맡는다.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { driverApi, driverError, type FleetSalaryMonth, type MySalaryMonth, type SalaryGroup, type SalaryLine } from '../lib/driverApi';
import Screen, { Empty, ErrorBanner, Loading } from './Screen';
import SalarySlip from './SalarySlip';
import { monthLabel, openAtLabel, won } from './format';

/**
 * 차주 — 법인이 다른 내 차량 전부의 차인지급액 합계(11-03 최찬영 「마이너스가 얼만지 뜨면 그걸 회사에 입금」).
 * 어느 기사 명세를 보고 있든 같은 숫자(서버가 로그인한 차주 기준으로 계산).
 */
function FleetTotal({ f }: { f: FleetSalaryMonth }) {
  const n = Number(f.result_amt);
  return (
    <section className="driver-card mb-4 border-2 border-brand-200 bg-brand-50 p-5">
      <p className="text-base font-semibold text-brand-700">내 차량 {f.car_cnt}대 전체 합계</p>
      <p className={`mt-1 text-2xl font-bold num ${n < 0 ? 'text-red-600' : 'text-ink'}`}>{won(f.result_amt)}원</p>
      <p className="mt-1 break-keep text-sm text-ink-soft">
        {n < 0 ? '마이너스 금액만큼 회사에 입금하실 금액입니다.' : '차량별 차인지급액을 모두 더한 금액입니다.'} 차량별 명세는 위 「누구 것을 볼까요?」에서 고르세요.
      </p>
    </section>
  );
}

export default function SalaryDetail() {
  const { mon } = useParams();
  const driveMon = mon ?? '';
  const [lines, setLines] = useState<SalaryLine[] | null>(null);
  const [groups, setGroups] = useState<SalaryGroup[]>([]);
  const [summary, setSummary] = useState<MySalaryMonth | null>(null);
  const [fleet, setFleet] = useState<FleetSalaryMonth | null>(null);
  /** 🔴 "줄이 0개"의 이유가 둘이다 — 아직 안 열렸거나, 정말 없거나. 섞으면 기사가 오해한다. */
  const [published, setPublished] = useState(true);
  const [openAt, setOpenAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(driveMon)) {
      setError('없는 명세서입니다.');
      return;
    }
    Promise.all([driverApi.listSalaryLines({ driveMon }), driverApi.listSalaryMonths()])
      .then(([l, m]) => {
        setLines(l.rows);
        setGroups(l.groups ?? []);
        setPublished(l.published !== false);
        setOpenAt(l.openAt ?? null);
        setSummary(m.rows.find((r) => r.drive_mon === driveMon) ?? null);
        setFleet((m.fleet ?? []).find((f) => f.drive_mon === driveMon && f.car_cnt > 1) ?? null);
      })
      .catch((e) => {
        setError(driverError(e, '명세서를 불러오지 못했습니다.'));
        setLines([]);
      });
  }, [driveMon]);

  return (
    <Screen title={monthLabel(driveMon)} back="/salary" acting>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {lines === null ? (
        <Loading />
      ) : !published ? (
        // 공개 전. **금액은 서버가 아예 안 내려준다** — 여기서 가리는 게 아니다.
        <section className="driver-card p-5 text-center">
          <p className="text-lg font-bold text-ink">아직 준비 중입니다</p>
          <p className="mt-2 text-base text-ink-soft">
            {openAtLabel(openAt)
              ? `${openAtLabel(openAt)} 부터 볼 수 있습니다.`
              : '정산 작업이 끝나면 열립니다.'}
          </p>
        </section>
      ) : lines.length === 0 ? (
        <Empty>이 달 명세서가 없습니다.</Empty>
      ) : (
        <>
          {fleet && <FleetTotal f={fleet} />}
          <SalarySlip lines={lines} summary={summary} groups={groups} />
        </>
      )}
    </Screen>
  );
}
